import { useState, useEffect, useMemo } from 'react';
import { AdvancedMultiSelectDropdown, LtModal, ExpandableTable, LtIcon, IndicatorModal, LtButton } from 'limetray-ui-kit';
import { userManagerConstants } from '../../../../constants/userManagerConstants';
import ApiConstants from '../../../../constants/apiConstants';
import http from '../../../../utils/http';
import LtLabel from '../../../../components/LtLabel';
import LtLoader from '../../../../components/LtLoader';

const { createUserModal } = userManagerConstants;

/**
 * UserRolesSection
 *
 * Renders the "Assign Role" section for user create/edit.
 * Uses the limetray-ui-kit's AdvancedMultiSelectDropdown to assign a single role group.
 */
function UserRolesSection({
  selectedRole, // format: string/number representing roleGroupId, or empty string/null
  onChange,
  mode,
  brandUserId,
  initialRole,
  flatPermissions = [],
  permissionsLoading = false,
}) {
  const [roles, setRoles] = useState([]);
  const [rawRoles, setRawRoles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const brandId = window?.LTD?.getSiteInfo?.()?.brandId || '1672';
      const url = ApiConstants.ROLE_GROUPS.GET_ALL.URL(brandId);
      // We pass size: 1000 to fetch all active roles
      const res = await http.get(url, {}, { offset: 0, size: 1000 });
      const fetchedRoles = res?.result?.roles || [];
      setRawRoles(fetchedRoles);

      // Filter active roles and map to format required by AdvancedMultiSelectDropdown
      const mapped = fetchedRoles
        .filter((r) => r.status === 'ACTIVE' || r.status === true) // support active statuses
        .map((r) => ({
          id: String(r.roleGroupId),
          label: r.roleGroupName,
        }));
      setRoles(mapped);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      setRoles([]);
      setRawRoles([]);
    }
  };

  const handleSelectRole = (selectedItems) => {
    const value = Array.isArray(selectedItems) ? selectedItems[0] : selectedItems;
    onChange(value?.id || value || '');
  };

  const handleDeleteRole = () => {
    setIsDeleteConfirmOpen(false);
    onChange('');
  };

  // Convert selectedRole to array for dropdown
  const defaultSelected = selectedRole ? [String(selectedRole)] : [];

  const selectedRoleObj = useMemo(() => {
    return rawRoles.find((r) => String(r.roleGroupId) === String(selectedRole));
  }, [rawRoles, selectedRole]);

  const tableData = useMemo(() => {
    if (!selectedRoleObj || !selectedRoleObj.roleIds || flatPermissions.length === 0) {
      return [];
    }
    return selectedRoleObj.roleIds
      .map((roleId) => {
        return flatPermissions.find((p) => String(p.roleId) === String(roleId));
      })
      .filter(Boolean);
  }, [selectedRoleObj, flatPermissions]);

  const columns = useMemo(() => [
    {
      header: createUserModal.sectionTitles.assignRole.tableHeaders.productName,
      accessor: (row) => row.productName || '',
      width: '35%',
    },
    {
      header: createUserModal.sectionTitles.assignRole.tableHeaders.featureName,
      accessor: (row) => row.featureName || '',
      width: '35%',
    },
    {
      header: createUserModal.sectionTitles.assignRole.tableHeaders.permission,
      accessor: (row) => row.roleName || '',
      width: '30%',
    },
  ], []);

  return (
    <div className="inputGrid">
      <div className="row">
        <div className="modal-form-field">
          <LtLabel>{createUserModal.labels.role}</LtLabel>
          <div className={`modal-role-dropdown ${!selectedRole ? 'is-placeholder' : ''}`}>
            <AdvancedMultiSelectDropdown
              options={roles}
              defaultSelectedItems={defaultSelected}
              isMultiSelect={false}
              isCollapsible={false}
              isSearchable={true}
              placeholder={createUserModal.sectionTitles.assignRole.placeholder}
              searchPlaceholder={createUserModal.sectionTitles.assignRole.searchPlaceholder}
              onSelect={handleSelectRole}
              selectedBgColor="#e8f5e9"
              hoverBgColor="#f0f4f8"
              borderRadius="8px"
              showSelectAll={false}
              removeBorderBetweenItems
            />
          </div>
        </div>
      </div>

      {selectedRoleObj && (
        <div className="selected-role-details-container">
          <div className="title">{createUserModal.sectionTitles.assignRole.detailsHeader}</div>
          <LtButton
            transparent
            customStyles={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '6px',
              position: 'absolute',
              right: '0',
              top: '5px'
            }}
            leftElement={
              <LtIcon
                useReactIcon
                reactIcon={userManagerConstants.icons.trashIcon}
                size="tiny"
              />
            }
            onClick={() => setIsDeleteConfirmOpen(true)}
          />
          <div className="details-grid">
            <div className="detail-item">
              <span className="label">{createUserModal.sectionTitles.assignRole.labels.roleName}</span>
              <span className="value">{selectedRoleObj.roleGroupName || ''}</span>
            </div>
            <div className="detail-item">
              <span className="label">{createUserModal.sectionTitles.assignRole.labels.description}</span>
              <span className="value">{selectedRoleObj.roleGroupDescription || ''}</span>
            </div>
            <div className="detail-item">
              <span className="label">{createUserModal.sectionTitles.assignRole.labels.totalPermissions}</span>
              <span
                className="value permissions-link"
                onClick={() => setIsModalOpen(true)}
              >
                {selectedRoleObj.roleIds ? selectedRoleObj.roleIds.length : 0}
              </span>
            </div>
            <div className="detail-item">
              <span className="label">{createUserModal.sectionTitles.assignRole.labels.roleStatus}</span>
              <span className={`value ${selectedRoleObj.status === 'ACTIVE' ? 'status-active' : 'status-inactive'}`}>
                {selectedRoleObj.status === 'ACTIVE'
                  ? createUserModal.sectionTitles.assignRole.labels.active
                  : createUserModal.sectionTitles.assignRole.labels.inactive}
              </span>
            </div>
          </div>
        </div>
      )}

      <LtModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        header={createUserModal.sectionTitles.assignRole.modalHeader}
        className="role-permissions-modal"
        width="800px"
      >
        <div className="role-permissions-modal-body">
          {permissionsLoading ? (
            <div className="loading-wrapper">
              <LtLoader size="medium" inline="centered" message={createUserModal.sectionTitles.assignRole.loadingMessage} />
            </div>
          ) : (
            <div className="table-wrapper">
              <ExpandableTable
                tableData={tableData}
                columns={columns}
                expandable={false}
                maxHeight="400px"
                paginationEnabled={false}
                tableHeaderStyles={{
                  background: 'white',
                }}
                tableHeaderCellStyles={{
                  color: '#6b7280',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
                tableBodyCellStyles={{
                  padding: '12px 16px',
                }}
                tableBodyStyles={{
                  borderRadius: '0'
                }}
                emptyMessage={createUserModal.sectionTitles.assignRole.emptyPermissions}
              />
            </div>
          )}
        </div>
      </LtModal>

      <IndicatorModal
        open={isDeleteConfirmOpen}
        title={createUserModal.deleteConfirm.roleAssignment.header}
        message={createUserModal.deleteConfirm.roleAssignment.content}
        variant="warning"
        onClose={() => setIsDeleteConfirmOpen(false)}
        actions={[
          {
            label: createUserModal.deleteConfirm.roleAssignment.cancelButton,
            onClick: () => setIsDeleteConfirmOpen(false),
            backgroundColor: '#ffffff',
						color: '#374151',
						borderColor: '#efefef',
          },
          {
            label: createUserModal.deleteConfirm.roleAssignment.confirmButton,
            onClick: handleDeleteRole,
            backgroundColor: '#ef4444',
            color: '#ffffff',
          },
        ]}
        actionsAlignment="center"
        maxWidth="420px"
      />
    </div>
  );
}

export default UserRolesSection;
